import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { FeatureActivationCongratsModalProps } from '@mm/state/modules/modalsState/props';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useCreateCampaignHandlers } from '../../../editor/features/beta/modules/Campaigns/use-create-campaign-handlers';
import { useCreateChatStarter } from '../../../editor/features/beta/modules/ChatStarters/use-create-chat-starter';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import {
  Countdown,
  ParsedTimeLeft,
} from '../../components/Countdown/Countdown';
import { trackWebAppGAEvent } from '../../helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../hooks/use-green-cue-markers';
import { useFreeTrialUpgradeModal } from '../FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ModalBody, ModalHeader } from '../modal-helpers';
import './FeatureActivationCongratsModal.pcss';

export const FeatureActivationCongratsModal = ({
  feature,
  onClose,
}: FeatureActivationCongratsModalProps) => {
  const { nextFeature } = useGreenCueMarkers();
  const { createChatStarter } = useCreateChatStarter();
  const { createByKind } = useCreateCampaignHandlers();
  const history = useHistory();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const text = (() => {
    switch (feature) {
      case 'sms':
        return `activating your SMS number`;
      case 'webchat':
        return `activating your website chat`;
      case 'comment_guard':
        return `activating your first Comment Guard!`;
      case 'drip_campaign':
        return `activating your first Drip Campaign`;
      case 'chat_blaster':
        return `sending your first chat blast.`;
      case 'notification_email':
        return `adding your first notification widget`;
      default:
        return '';
    }
  })();

  const onActivate = (
    kind:
      | UIFilterStarterKindType
      | 'drip_campaigns'
      | 'chat_blaster'
      | 'zapier',
  ) => {
    modalActions.closeModal();
    if (kind === 'drip_campaigns' || kind === 'chat_blaster') {
      trackWebAppGAEvent('click', {
        category: 'create_campaign',
        label: `feature_activation_congrats_modal_go_button_${kind}`,
      });
      createByKind(kind);
      return;
    }
    if (kind === 'zapier') {
      return history.push(
        BotBuilderRoutes.integrations.getLink({ chatbotId }),
        {
          kind: 'native-integrations',
        },
      );
    }
    trackWebAppGAEvent('click', {
      category: 'create_starter',
      label: `feature_activation_congrats_modal_go_button_${kind}`,
    });
    createChatStarter(kind);
  };

  const nextFeatureOptions = (() => {
    switch (nextFeature) {
      case 'webchat':
        return {
          text: `your Website Chat so you can generate leads from your website`,
          action: () => {
            onActivate('customer_chat_widget');
          },
        };
      case 'comment_guard':
        return {
          text: `a Comment Guard so your bot can send messages to people who commented on your FB posts`,
          action: () => {
            onActivate('comment_guard');
          },
        };
      case 'drip_campaign':
        return {
          text: `a Drip Campaign to re-engage with your leads after a conversation has ended`,
          action: () => {
            onActivate('drip_campaigns');
          },
        };
      case 'chat_blaster':
        return {
          text: `a Chat Blast`,
          action: () => {
            onActivate('chat_blaster');
          },
        };
      case 'zapier':
        return {
          text: `a Zapier integration to send your leads to a CRM`,
          action: () => {
            onActivate('zapier');
          },
        };
      case undefined:
      default:
        return undefined;
    }
  })();

  const facebookPage = useSelector(activeFacebookPageSelector);

  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;

  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const modalActions = useModalActions();

  const [previousExpiresAt, setPreviousExpiresAt] = useState<string>();
  const [parsedTimeLeft, setParsedTimeLeft] = useState<ParsedTimeLeft>();

  const { isFreeTrialPromotionWithoutSubscription } =
    useFreeTrialUpgradeModal();

  const expiresAt = facebookPage?.redemption?.freeTrial
    ? facebookPage?.redemption?.expiresAt
    : undefined;

  useEffect(() => {
    const updateState = async () => {
      if (facebookPage && expiresAt) {
        await connectedPageCRUDActions.retrievePage(facebookPage.id);
        setPreviousExpiresAt(expiresAt);
      }
    };
    updateState();
  }, []);

  if (!(previousExpiresAt && expiresAt)) return null;

  if (
    !showActivationCues ||
    !isFreeTrialPromotionWithoutSubscription ||
    previousExpiresAt === expiresAt
  ) {
    modalActions.closeModal();
    onClose?.();
    return null;
  }

  const showChaining = feature !== 'notification_email';

  return (
    <div>
      <ModalHeader
        className="feature-activation-congrats-modal__header"
        childrenContainerClassName="feature-activation-congrats-modal__header--children"
        closeOverride={() => {
          modalActions.closeModal();
          onClose?.();
        }}
      >
        <div className="feature-activation-congrats-modal__header-content">
          <div className="feature-activation-congrats-modal__header-content--title">
            Nice job!
          </div>
          <div className="feature-activation-Vcongrats-modal__header-content--text">
            You got 1 FREE day for {text}
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="feature-activation-congrats-modal__body">
          <div className="feature-activation-congrats-modal__body--title">
            Time remaining on your free trial
          </div>
          <Countdown
            expiresAt={expiresAt}
            parsedTimeLeft={parsedTimeLeft}
            setParsedTimeLeft={setParsedTimeLeft}
            previousExpiresAt={previousExpiresAt}
          />
          {showChaining && nextFeatureOptions && (
            <div className="feature-activation-congrats-modal__body--daisy-chaining">
              <div className="feature-activation-congrats-modal__body--daisy-chaining__text">
                Now let's set up {nextFeatureOptions.text}
              </div>
              <PrimaryButton onClick={nextFeatureOptions.action}>
                Let's Go!
              </PrimaryButton>
            </div>
          )}
        </div>
      </ModalBody>
    </div>
  );
};
